import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32, NO_HANDLE } from "./limits.ts";

/**
 * The `projectiles` frame (ADR-095 decisions 11 and 12, PROTOCOL.md 5.18): server to client, one a tick of the shots a
 * page may see, a header of the tick and a count, then 19 bytes a launch. No position stream follows: the flight is a
 * straight line from the origin to the landing point at a constant rate over `flightTicks`, so the launch is all a page
 * needs. A map server writes launches and then the header into a buffer the session allocates at its join, through plain
 * byte stores, so a send allocates nothing; the decoder never throws (ADR-030).
 */

/**
 * The header: the common three bytes, the `u32` tick and the `u8` count.
 */
export const PROJECTILES_HEADER_BYTES = HEADER_BYTES + 4 + 1;

/**
 * One launch: id, source, target, ability, origin x and y, landing x and y (`u16` each), then flight, age and flags
 * (`u8` each).
 */
export const PROJECTILE_LAUNCH_BYTES = 19;

/**
 * The most launches a map server puts in one session's frame (ADR-095 decision 15); a page reads up to 255.
 */
export const MAX_PROJECTILE_RECORDS = 32;

/**
 * The source of a launch whose shooter the page's last sent view does not hold, and the target of every launch until a
 * homing path names one.
 */
export const NO_SOURCE = NO_HANDLE;

const MAX_COUNT = 255;
const MAX_BYTE = 255;

/**
 * One launch, decoded or to be encoded.
 */
export interface ProjectileLaunch {
  id: number;
  source: number;
  target: number;
  ability: number;
  originX: number;
  originY: number;
  landingX: number;
  landingY: number;
  flightTicks: number;
  age: number;
  flags: number;
}

/**
 * A decoded `projectiles` frame, reused across frames: its tick, how many launches it held, and the launches, the first
 * `count` of `launches` valid.
 */
export interface ProjectilesRecord {
  tick: number;
  count: number;
  readonly launches: ProjectileLaunch[];
}

/**
 * A fresh record for `decodeProjectiles` to fill.
 *
 * @returns An empty record.
 */
export function newProjectilesRecord(): ProjectilesRecord {
  return { tick: 0, count: 0, launches: [] };
}

/**
 * Bytes of a frame of a number of launches.
 *
 * @param count The launches.
 * @returns `8 + 19 * count`.
 */
export function projectilesBytes(count: number): number {
  return PROJECTILES_HEADER_BYTES + PROJECTILE_LAUNCH_BYTES * count;
}

/**
 * Writes one launch at its place in a frame through plain byte stores. The caller has sized the buffer and keeps every
 * field within its width, as a map server does with values it computed.
 *
 * @param buffer The frame.
 * @param index The launch's place, from 0.
 * @param id The shot's id.
 * @param source The shooter's handle, or `NO_SOURCE`.
 * @param target The entity a homing path follows, `NO_SOURCE` for none.
 * @param ability The ability's number.
 * @param originX Where the flight starts, in sixteenths.
 * @param originY Where the flight starts.
 * @param landingX Where it lands.
 * @param landingY Where it lands.
 * @param flightTicks Ticks from the launch to the landing, 1 to 255.
 * @param age Ticks since the launch at the frame's tick.
 * @param flags 0; a page ignores bits it does not know.
 */
export function putProjectileLaunch(buffer: Uint8Array, index: number, id: number, source: number, target: number, ability: number, originX: number, originY: number, landingX: number, landingY: number, flightTicks: number, age: number, flags: number): void {
  const at = PROJECTILES_HEADER_BYTES + PROJECTILE_LAUNCH_BYTES * index;
  putU16(buffer, at, id);
  putU16(buffer, at + 2, source);
  putU16(buffer, at + 4, target);
  putU16(buffer, at + 6, ability);
  putU16(buffer, at + 8, originX);
  putU16(buffer, at + 10, originY);
  putU16(buffer, at + 12, landingX);
  putU16(buffer, at + 14, landingY);
  buffer[at + 16] = flightTicks;
  buffer[at + 17] = age;
  buffer[at + 18] = flags;
}

/**
 * Writes a frame's header once its launches are in place.
 *
 * @param buffer The frame.
 * @param tick The tick the frame is sent in.
 * @param count The launches written, 1 to 255.
 * @returns The frame's length in bytes.
 */
export function putProjectilesHeader(buffer: Uint8Array, tick: number, count: number): number {
  putHeader(buffer, MessageType.Projectiles);
  putU32(buffer, HEADER_BYTES, tick);
  buffer[HEADER_BYTES + 4] = count;
  return projectilesBytes(count);
}

/**
 * Encodes a `projectiles` frame into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `projectilesBytes(launches.length)` long.
 * @param tick The tick.
 * @param launches 1 to 255 launches.
 * @returns Bytes written.
 * @throws Error when the count is 0 or above 255, the buffer is too small, or a field does not fit its width (a flight
 *   of 1 to 255).
 */
export function encodeProjectiles(buffer: Uint8Array, tick: number, launches: readonly Readonly<ProjectileLaunch>[]): number {
  if (launches.length < 1 || launches.length > MAX_COUNT) {
    throw new Error(`projectiles count ${launches.length} is not 1 to ${MAX_COUNT}`);
  }
  const length = projectilesBytes(launches.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte projectiles frame`);
  }
  assertField(tick, MAX_U32, `projectiles tick ${tick}`);
  for (let index = 0; index < launches.length; index += 1) {
    const launch = launches[index] as Readonly<ProjectileLaunch>;
    assertField(launch.id, MAX_U16, `projectile id ${launch.id}`);
    assertField(launch.source, MAX_U16, `projectile source ${launch.source}`);
    assertField(launch.target, MAX_U16, `projectile target ${launch.target}`);
    assertField(launch.ability, MAX_U16, `projectile ability ${launch.ability}`);
    assertField(launch.originX, MAX_U16, `projectile originX ${launch.originX}`);
    assertField(launch.originY, MAX_U16, `projectile originY ${launch.originY}`);
    assertField(launch.landingX, MAX_U16, `projectile landingX ${launch.landingX}`);
    assertField(launch.landingY, MAX_U16, `projectile landingY ${launch.landingY}`);
    if (!Number.isInteger(launch.flightTicks) || launch.flightTicks < 1 || launch.flightTicks > MAX_BYTE) {
      throw new Error(`projectile flightTicks ${launch.flightTicks} is not 1 to ${MAX_BYTE}`);
    }
    assertField(launch.age, MAX_BYTE, `projectile age ${launch.age}`);
    assertField(launch.flags, MAX_BYTE, `projectile flags ${launch.flags}`);
    putProjectileLaunch(buffer, index, launch.id, launch.source, launch.target, launch.ability, launch.originX, launch.originY, launch.landingX, launch.landingY, launch.flightTicks, launch.age, launch.flags);
  }
  return putProjectilesHeader(buffer, tick, launches.length);
}

/**
 * Decodes a `projectiles` frame into a record. Never throws: a frame shorter than its header, a count of 0, a length
 * other than `8 + 19 * count` or another type is malformed, and another version a mismatch. Every value of every field is
 * well formed, a flight of 0 and unknown flags among them, for the page to judge.
 *
 * @param frame The whole frame.
 * @param out Record to fill; untouched unless the verdict is `ok`.
 * @returns The verdict.
 */
export function decodeProjectiles(frame: Uint8Array, out: ProjectilesRecord): DecodeVerdict {
  if (frame.length < PROJECTILES_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[HEADER_BYTES + 4] as number;
  if (count === 0) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.Projectiles, projectilesBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  const launches = out.launches;
  for (let index = 0; index < count; index += 1) {
    const at = PROJECTILES_HEADER_BYTES + PROJECTILE_LAUNCH_BYTES * index;
    let launch = launches[index];
    if (launch === undefined) {
      launch = { id: 0, source: 0, target: 0, ability: 0, originX: 0, originY: 0, landingX: 0, landingY: 0, flightTicks: 0, age: 0, flags: 0 };
      launches.push(launch);
    }
    launch.id = readU16(frame, at);
    launch.source = readU16(frame, at + 2);
    launch.target = readU16(frame, at + 4);
    launch.ability = readU16(frame, at + 6);
    launch.originX = readU16(frame, at + 8);
    launch.originY = readU16(frame, at + 10);
    launch.landingX = readU16(frame, at + 12);
    launch.landingY = readU16(frame, at + 14);
    launch.flightTicks = frame[at + 16] as number;
    launch.age = frame[at + 17] as number;
    launch.flags = frame[at + 18] as number;
  }
  out.tick = readU32(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
